/**
 * Edition d'une cascade de parts, partagee par la saisie d'une operation et par
 * l'ecran des modeles de repartition — qui doivent se comporter a l'identique.
 */

import { Fragment } from 'react'

import { decrireRegle, type PartCalculable, type Repartition } from '../analysis/repartition'
import type { SaisiePart } from '../db/repo'
import { formatMontant } from '../domain/argent'
import type { BaseePart, Id, ModePart } from '../domain/types'
import { ChampMontant, Encart, IconePoubelle, SelectTiers } from './composants'
import { useDonnees } from './donnees'

/** Les quatre regles telles qu'on les choisit a l'ecran. Le moteur distingue
 *  `mode` et `base` ; l'utilisateur, lui, pense « 50 % du reste ». */
type Regle = 'montant' | 'pct-total' | 'pct-reste' | 'solde'

const REGLES: { valeur: Regle; libelle: string }[] = [
  { valeur: 'montant', libelle: 'Montant fixe' },
  { valeur: 'pct-reste', libelle: '% du reste' },
  { valeur: 'pct-total', libelle: '% du total' },
  { valeur: 'solde', libelle: 'Solde (tout le reste)' },
]

function versRegle(p: { mode: ModePart; base: BaseePart }): Regle {
  if (p.mode === 'montant') return 'montant'
  if (p.mode === 'solde') return 'solde'
  return p.base === 'reste' ? 'pct-reste' : 'pct-total'
}

function depuisRegle(r: Regle): { mode: ModePart; base: BaseePart } {
  switch (r) {
    case 'montant':
      return { mode: 'montant', base: 'total' }
    case 'solde':
      return { mode: 'solde', base: 'reste' }
    case 'pct-total':
      return { mode: 'pourcentage', base: 'total' }
    case 'pct-reste':
      return { mode: 'pourcentage', base: 'reste' }
  }
}

/** Part en cours d'edition. `cle` est locale a l'ecran ; `id` n'existe que pour
 *  une part deja enregistree, et c'est lui qui preserve ses reglements. */
export interface PartEdition extends SaisiePart {
  cle: string
}

let compteurCle = 0
export const cle = () => `p${++compteurCle}`


export function nouvellePart(tiersId: Id, ordre: number): PartEdition {
  return { cle: cle(), tiersId, ordre, mode: 'montant', base: 'total', valeur: 0, libelle: '' }
}

/** L'ordre affiche fait l'ordre de la cascade : un rang par ligne. */
export function versCalculables(parts: readonly PartEdition[]): (PartCalculable & { libelle: string })[] {
  return parts.map((p, i) => ({ ...p, ordre: i + 1 }))
}

export function EditeurParts({
  parts,
  onChange,
}: {
  parts: PartEdition[]
  onChange: (f: (p: PartEdition[]) => PartEdition[]) => void
}) {
  const { d } = useDonnees()
  const modifier = (c: string, patch: Partial<PartEdition>) => onChange((l) => l.map((x) => (x.cle === c ? { ...x, ...patch } : x)))

  return (
    <>
      {parts.map((p, i) => {
        const regle = versRegle(p)
        return (
          <div key={p.cle} className="part">
            <div className="part__entete">
              <span className="part__rang">{i + 1}</span>
              <div className="grandir">
                <SelectTiers tiers={d.tiers} valeur={p.tiersId || null} vide="Qui ?" onChange={(t) => modifier(p.cle, { tiersId: t ?? '' })} />
              </div>
              <button type="button" className="btn btn--fantome btn--petit" aria-label="Retirer la part" onClick={() => onChange((l) => l.filter((x) => x.cle !== p.cle))}>
                <IconePoubelle />
              </button>
            </div>
            <div className="grille-2">
              <select
                aria-label="Règle"
                value={regle}
                onChange={(e) => {
                  const r = e.target.value as Regle
                  modifier(p.cle, { ...depuisRegle(r), valeur: r === 'pct-reste' || r === 'pct-total' ? 50 : 0 })
                }}
              >
                {REGLES.map((r) => (
                  <option key={r.valeur} value={r.valeur}>{r.libelle}</option>
                ))}
              </select>
              {regle === 'montant' ? (
                <ChampMontant valeur={p.valeur || null} onChange={(c) => modifier(p.cle, { valeur: c ?? 0 })} />
              ) : regle === 'solde' ? (
                <span className="doux" style={{ alignSelf: 'center' }}>ce qui reste</span>
              ) : (
                <ChampPourcentage valeur={p.valeur} onChange={(v) => modifier(p.cle, { valeur: v })} />
              )}
            </div>
          </div>
        )
      })}
    </>
  )
}

/** Saisie libre (« 50 », « 33,3 ») validee a la volee, bornee a 0-100. */
function ChampPourcentage({ valeur, onChange }: { valeur: number; onChange: (v: number) => void }) {
  return (
    <div className="ligne" style={{ gap: 6 }}>
      <input
        type="text"
        inputMode="decimal"
        aria-label="Pourcentage"
        defaultValue={String(valeur)}
        onChange={(e) => {
          const v = Number(e.target.value.replace(',', '.').replace('%', '').trim())
          if (e.target.value.trim() !== '' && Number.isFinite(v)) onChange(Math.min(100, Math.max(0, v)))
        }}
      />
      <span className="doux">%</span>
    </div>
  )
}

export function ApercuCascade({ apercu }: { apercu: Repartition<PartCalculable> }) {
  const { ix, d } = useDonnees()
  return (
    <div className="defilable">
      <table className="cascade">
        <tbody>
          <tr>
            <td>Total payé</td>
            <td className="nombre">{formatMontant(apercu.total)}</td>
          </tr>
          {apercu.lignes.map((l, i) => {
            const suivante = apercu.lignes[i + 1]
            const finPalier = !suivante || suivante.part.ordre !== l.part.ordre
            return (
              <Fragment key={i}>
                <tr className={l.part.tiersId === d.moiId ? 'cascade__moi' : undefined}>
                  <td>
                    {ix.tiersParId.get(l.part.tiersId)?.nom ?? '—'}{' '}
                    <span className="tres-doux">
                      {decrireRegle(l.part)}
                      {l.part.mode === 'pourcentage' ? ` de ${formatMontant(l.assiette)}` : ''}
                    </span>
                  </td>
                  <td className="nombre">−{formatMontant(l.montant)}</td>
                </tr>
                {finPalier && l.part.mode !== 'solde' && (
                  <tr className="cascade__reste">
                    <td>reste</td>
                    <td className="nombre">{formatMontant(l.resteApres)}</td>
                  </tr>
                )}
              </Fragment>
            )
          })}
          <tr className="cascade__moi">
            <td>À ma charge</td>
            <td className="nombre">{formatMontant(apercu.partMoi)}</td>
          </tr>
        </tbody>
      </table>
      {apercu.avertissements.map((a) => (
        <Encart key={a} ton="alerte">{a}</Encart>
      ))}
    </div>
  )
}
